import { useRef, useState } from 'react';
import { Box, Typography, Button, Chip } from '@mui/material';
import { CloudUpload, Image } from '@mui/icons-material';
import { useAuth } from '@/context/AuthContext';
import { uploadClientSplash } from '@/api/client.service';
import { useNotification } from '@/context/NotificationContext';
import { ConfigDropdownForm } from '@/pages/config/ConfigDropdownForm';

const MAX_SIZE_BYTES = 10 * 1024 * 1024;
const ACCEPTED_TYPES = ['image/jpeg', 'image/png', 'image/webp'];

interface SplashConfigPageProps {
  clientId?: string;
  clientName?: string;
  initialSplashUrl?: string | null;
  initialSplashUrlDark?: string | null;
  defaultOpen?: boolean;
  onChangeClient?: () => void;
}

interface SplashImagePickerProps {
  label: string;
  helperText: string;
  preview: string | null;
  onFileSelected: (file: File) => void;
}

const SplashImagePicker = ({ label, helperText, preview, onFileSelected }: SplashImagePickerProps) => {
  const { notify } = useNotification();
  const inputRef = useRef<HTMLInputElement>(null);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!ACCEPTED_TYPES.includes(file.type)) {
      notify('Formato inválido. Use JPEG, PNG ou WebP.', 'error');
      return;
    }
    if (file.size > MAX_SIZE_BYTES) {
      notify('Arquivo muito grande. Máximo 10 MB.', 'error');
      return;
    }

    onFileSelected(file);
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1.5, width: '100%' }}>
      <Typography variant="subtitle2" sx={{ fontWeight: 600, color: 'text.primary', alignSelf: 'flex-start' }}>
        {label}
      </Typography>
      <Box
        sx={{
          width: '100%',
          maxWidth: 480,
          aspectRatio: '1/1',
          borderRadius: 3,
          border: '2px dashed rgba(100,116,139,0.3)',
          bgcolor: 'rgba(248,250,252,0.8)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          overflow: 'hidden',
          cursor: 'pointer',
        }}
        onClick={() => inputRef.current?.click()}
      >
        {preview ? (
          <Box
            component="img"
            src={preview}
            alt={`${label} preview`}
            sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
          />
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1, color: 'grey.500' }}>
            <Image sx={{ fontSize: 48 }} />
            <Typography variant="body2">Clique para selecionar uma imagem</Typography>
          </Box>
        )}
      </Box>

      <input
        ref={inputRef}
        type="file"
        accept={ACCEPTED_TYPES.join(',')}
        style={{ display: 'none' }}
        onChange={handleFileChange}
      />

      <Button
        variant="outlined"
        startIcon={<CloudUpload />}
        onClick={() => inputRef.current?.click()}
        sx={{ borderRadius: 2, fontWeight: 600 }}
      >
        {preview ? 'Alterar imagem' : 'Selecionar imagem'}
      </Button>

      <Typography variant="caption" sx={{ color: 'grey.500', textAlign: 'center' }}>
        {helperText}
      </Typography>
    </Box>
  );
};

export const SplashConfigPage = ({
  clientId,
  clientName,
  initialSplashUrl,
  initialSplashUrlDark,
  defaultOpen,
  onChangeClient,
}: SplashConfigPageProps) => {
  const { user, setLoggedClient } = useAuth();
  const { notify } = useNotification();
  const [lightPreview, setLightPreview] = useState<string | null>(
    initialSplashUrl ?? user?.loggedClient?.splash_url ?? null
  );
  const [darkPreview, setDarkPreview] = useState<string | null>(
    initialSplashUrlDark ?? user?.loggedClient?.splash_url_dark ?? null
  );
  const [lightFile, setLightFile] = useState<File | null>(null);
  const [darkFile, setDarkFile] = useState<File | null>(null);
  const [loading, setLoading] = useState(false);

  const selectFile = (file: File, setFile: (f: File) => void, setPreview: React.Dispatch<React.SetStateAction<string | null>>) => {
    setFile(file);
    setPreview(prev => {
      if (prev && prev.startsWith('blob:')) URL.revokeObjectURL(prev);
      return URL.createObjectURL(file);
    });
  };

  const handleSave = async () => {
    const cid = clientId ?? user?.loggedClient?.id;
    if ((!lightFile && !darkFile) || !cid) return;
    setLoading(true);
    try {
      let updated;
      if (lightFile) updated = await uploadClientSplash(cid, lightFile, 'light');
      if (darkFile) updated = await uploadClientSplash(cid, darkFile, 'dark');
      if (updated && !clientId) setLoggedClient(updated);
      setLightFile(null);
      setDarkFile(null);
      notify('Arte de carregamento salva com sucesso!');
    } catch (err) {
      notify('Erro ao salvar a imagem. Tente novamente.', 'error');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <ConfigDropdownForm
      title="Arte de Carregamento"
      subtitle={clientName}
      onSave={lightFile || darkFile ? handleSave : undefined}
      loading={loading}
      defaultOpen={defaultOpen}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4 }}>
        {onChangeClient && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, alignSelf: 'flex-start' }}>
            <Chip size="small" label={`Cliente: ${clientName}`} color="primary" variant="outlined" />
            <Button size="small" onClick={onChangeClient} sx={{ textTransform: 'none', fontSize: '0.75rem' }}>
              Trocar cliente
            </Button>
          </Box>
        )}

        <SplashImagePicker
          label="Tema claro (padrão)"
          helperText="JPEG, PNG ou WebP · Máximo 10 MB · Proporção recomendada 1:1"
          preview={lightPreview}
          onFileSelected={(file) => selectFile(file, setLightFile, setLightPreview)}
        />

        <SplashImagePicker
          label="Tema escuro (opcional)"
          helperText="Usada no app quando o dispositivo estiver em modo escuro. Se não for definida, a arte do tema claro é usada."
          preview={darkPreview}
          onFileSelected={(file) => selectFile(file, setDarkFile, setDarkPreview)}
        />
      </Box>
    </ConfigDropdownForm>
  );
};
